import { AppType, EmailDestinationApp } from "client/Api/Apps/common";
import {
	FormItem,
	Select,
	SelectItem,
	ValidationError
} from "client/Components";
import { AppDestinationWindowSelectFormItem } from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import { IFormHandler, useFormHandler } from "client/Forms";
import { createValidationConfiguration } from "client/Validation";
import { isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useImperativeHandle,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import { useSaveApp, EditAppSavedResult } from "../useSaveApp";
import {
	EditEmailAppViewModel,
	SaveEmailAppModel,
	saveEmailApp
} from "client/Api/Apps/EditApp/saveApp";

export type EditEmailAppProps = {
	initialSettings: EditAppSettings<EditEmailAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditEmailApp = forwardRef(
	(
		props: EditEmailAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const [formData, setFormData] = useState<SaveEmailAppModel>(
			() => convertToSaveModel(initialSettings)
		);

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const {
			canBeConfigured
		} = app;

		const formHandler: IFormHandler<SaveEmailAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const prepareModelForSaving = useCallback(
			(model: SaveEmailAppModel): SaveEmailAppModel => {
				return {
					...model,
					destinationApp: canBeConfigured
						? model.destinationApp
						: null
				};
			},
			[canBeConfigured]
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveEmailAppModel>(
			formHandler,
			saveEmailApp,
			AppType.Email,
			prepareModelForSaving
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under("destinationApp", "destinationWindow")
			.attachToTab()
			.hasValidationError;

		return (
			<EditAppBase
				formHandler={formHandler}
				hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
				initialAppSettings={app}
				usersAndUserGroupsData={usersAndUserGroupsData}
				initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
			>
				{canBeConfigured && (
					<FormItem
						label={localize("_DefaultClient")}
					>
						<Select
							{...formHandler.for("destinationApp")
								.attachToSelect()
							}
							options={emailDestinationApps}
							menuTitle={localize("_DefaultClient")}
						/>
						<ValidationError
							{...formHandler.for("destinationApp")
								.attachToValidationError()
							}
						/>
					</FormItem>
				)}
				<AppDestinationWindowSelectFormItem
					attachable={formHandler.for("destinationWindow")}
				/>
			</EditAppBase>
		);
	}
);
EditEmailApp.displayName = "EditEmailApp";

const validation = createValidationConfiguration<SaveEmailAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

const emailDestinationApps: SelectItem<EmailDestinationApp>[] = [
	{ text: localize("_EmailApp"), value: EmailDestinationApp.Email },
	{ text: localize("_OutlookWebApp"), value: EmailDestinationApp.OutlookWebApp }
];

function convertToSaveModel(
	initialSettings: EditAppSettings<EditEmailAppViewModel>
): SaveEmailAppModel {
	const {
		destinationWindow,
		destinationApp
	} = initialSettings
		.model
		.app;

	return {
		...initialGenericState(initialSettings),
		destinationWindow,
		destinationApp
	};
}